import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { Layout } from "./components/layout/Layout";

// Public Pages
import Home from "./pages/Home";
import About from "./pages/About";
import Membership from "./pages/Membership";
import Benefits from "./pages/Benefits";
import Resources from "./pages/Resources";
import Education from "./pages/Education";
import Events from "./pages/Events";
import Advocacy from "./pages/Advocacy";
import PartnersLanding from "./pages/PartnersLanding";
import PartnersCategory from "./pages/PartnersCategory";
import PartnerDetail from "./pages/PartnerDetail";
import PartnersSearch from "./pages/PartnersSearch";
import News from "./pages/News";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Directory from "./pages/Directory";
import Referrals from "./pages/Referrals";
import Jobs from "./pages/Jobs";
import PartnerDiscounts from "./pages/PartnerDiscounts";
import AffiliateProgram from "./pages/AffiliateProgram";
import AffiliateLanding from "./pages/AffiliateLanding";
import PartnerDashboard from "./pages/PartnerDashboard";
import AdminAffiliateRules from "./pages/AdminAffiliateRules";
import AffiliateInfluencers from "./pages/AffiliateInfluencers";
import AffiliateBrokerages from "./pages/AffiliateBrokerages";
import AffiliateApply from "./pages/AffiliateApply";
import AffiliateTerms from "./pages/AffiliateTerms";
import AdminAffiliates from "./pages/AdminAffiliates";
import AdminCommissionStrategy from "./pages/AdminCommissionStrategy";
import AdminRevenueProjections from "./pages/AdminRevenueProjections";
import AdminGrowthPlaybook from "./pages/AdminGrowthPlaybook";
import AdminFraudFlags from "./pages/AdminFraudFlags";
import AdminAffiliateEmails from "./pages/AdminAffiliateEmails";
import AdminPayouts from "./pages/AdminPayouts";
import AdminPartners from "./pages/AdminPartners";
import AdminScheduledReports from "./pages/AdminScheduledReports";

// Auth Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import RegisterSuccess from "./pages/RegisterSuccess";
import AuthCallback from "./pages/AuthCallback";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";

// Dashboard Pages
import Dashboard from "./pages/Dashboard";
import DashboardProfile from "./pages/DashboardProfile";
import DashboardDirectory from "./pages/DashboardDirectory";
import DashboardAnnouncements from "./pages/DashboardAnnouncements";
import DashboardSettings from "./pages/DashboardSettings";
import MoxeyOS from "./pages/MoxeyOS";
import MoxeyAI from "./pages/MoxeyAI";
import MoxeyApp from "./pages/MoxeyApp";
import HireMoxey from "./pages/HireMoxey";
import MoxeyHome from "./pages/MoxeyHome";

// Community Pages
import Community from "./pages/Community";
import GroupDetail from "./pages/GroupDetail";
import CreateGroup from "./pages/CreateGroup";
import MemberProfile from "./pages/MemberProfile";
import Messages from "./pages/Messages";

// Split Report Pages
import SplitReportDashboard from "./pages/SplitReportDashboard";
import SplitReportListings from "./pages/SplitReportListings";
import SplitReportNewListing from "./pages/SplitReportNewListing";
import SplitReportListingDetail from "./pages/SplitReportListingDetail";

// Admin Pages
import Admin from "./pages/Admin";

// Scroll to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Wrapper to handle auth callback detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment (not query params) for session_id - CRITICAL for auth
  // This must happen during render, NOT in useEffect to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes with Layout */}
      <Route path="/" element={<Layout><Home /></Layout>} />
      <Route path="/membership" element={<Layout><Membership /></Layout>} />
      <Route path="/resources" element={<Layout><Resources /></Layout>} />
      <Route path="/advocacy" element={<Layout><Advocacy /></Layout>} />
      <Route path="/partners" element={<Layout><PartnersLanding /></Layout>} />
      <Route path="/partners/search" element={<Layout><PartnersSearch /></Layout>} />
      <Route path="/partners/category/:slug" element={<Layout><PartnersCategory /></Layout>} />
      <Route path="/partners/category/:slug/:subcategorySlug" element={<Layout><PartnersCategory /></Layout>} />
      <Route path="/partners/:slug" element={<Layout><PartnerDetail /></Layout>} />
      <Route path="/contact" element={<Layout><Contact /></Layout>} />
      <Route path="/faq" element={<Layout><FAQ /></Layout>} />
      <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
      <Route path="/terms" element={<Layout><Terms /></Layout>} />
      <Route path="/moxey" element={<MoxeyAI />} />
      <Route path="/moxey-ai" element={<Layout><MoxeyHome /></Layout>} />
      <Route path="/hire-moxey" element={<HireMoxey />} />
      <Route path="/app" element={<MoxeyApp />} />
      
      {/* Affiliate Public Routes (standalone pages, no layout) */}
      <Route path="/affiliate" element={<AffiliateLanding />} />
      <Route path="/affiliate/influencers" element={<AffiliateInfluencers />} />
      <Route path="/affiliate/brokerages" element={<AffiliateBrokerages />} />
      <Route path="/affiliate/apply" element={<AffiliateApply />} />
      <Route path="/affiliate/terms" element={<AffiliateTerms />} />
      
      {/* Auth Routes (no layout) */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/register/success" element={<RegisterSuccess />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      
      {/* Community Routes (members only) */}
      <Route path="/community" element={<Layout><Community /></Layout>} />
      <Route path="/community/messages" element={<Layout><Messages /></Layout>} />
      <Route path="/community/groups/create" element={<Layout><CreateGroup /></Layout>} />
      <Route path="/community/groups/:groupId" element={<Layout><GroupDetail /></Layout>} />
      <Route path="/community/member/:userId" element={<Layout><MemberProfile /></Layout>} />
      
      {/* Split Report Routes (licensed RE agents/brokers only) */}
      <Route path="/split-report" element={<Layout><SplitReportDashboard /></Layout>} />
      <Route path="/split-report/listings" element={<Layout><SplitReportListings /></Layout>} />
      <Route path="/split-report/new" element={<Layout><SplitReportNewListing /></Layout>} />
      <Route path="/split-report/listings/:listingId" element={<Layout><SplitReportListingDetail /></Layout>} />
      
      {/* Dashboard Routes */}
      <Route path="/dashboard" element={<Dashboard />}>
        <Route path="profile" element={<DashboardProfile />} />
        <Route path="directory" element={<DashboardDirectory />} />
        <Route path="resources" element={<Resources />} />
        <Route path="events" element={<Events />} />
        <Route path="announcements" element={<DashboardAnnouncements />} />
        <Route path="settings" element={<DashboardSettings />} />
        <Route path="moxey" element={<MoxeyOS />} />
        <Route path="partner" element={<PartnerDashboard />} />
      </Route>
      <Route path="/opportunities" element={<Dashboard />} />

      {/* Admin Routes */}
      <Route path="/admin" element={<Layout><Admin /></Layout>} />
      <Route path="/admin/settings/affiliate-rules" element={<Layout><AdminAffiliateRules /></Layout>} />
      <Route path="/admin/affiliates" element={<Layout><AdminAffiliates /></Layout>} />
      <Route path="/admin/commission-strategy" element={<Layout><AdminCommissionStrategy /></Layout>} />
      <Route path="/admin/revenue-projections" element={<Layout><AdminRevenueProjections /></Layout>} />
      <Route path="/admin/growth-playbook" element={<Layout><AdminGrowthPlaybook /></Layout>} />
      <Route path="/admin/fraud-flags" element={<Layout><AdminFraudFlags /></Layout>} />
      <Route path="/admin/affiliate-emails" element={<Layout><AdminAffiliateEmails /></Layout>} />
      <Route path="/admin/payouts" element={<Layout><AdminPayouts /></Layout>} />
      <Route path="/admin/partners" element={<Layout><AdminPartners /></Layout>} />
      <Route path="/admin/scheduled-reports" element={<Layout><AdminScheduledReports /></Layout>} />

      {/* Catch-all redirect */}
      <Route path="*" element={<Layout><Home /></Layout>} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppRouter />
        </BrowserRouter>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
